import { useParams, Navigate, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import RemodelCategories from '../data/RemodelCategories.json';
import { Helmet } from 'react-helmet-async';

export default function FeaturedCategory() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Add this log to see what you actually get
    console.log('RemodelCategories import:', RemodelCategories);

    // Defensive: handle both default and direct array
    let categories = RemodelCategories;
    if (Array.isArray(RemodelCategories.default)) {
      categories = RemodelCategories.default;
    } else if (Array.isArray(RemodelCategories)) {
      categories = RemodelCategories;
    } else {
      categories = [];
    }

    console.log(
      'Looking for slug:',
      slug,
      'in',
      categories.map((c) => c.slug)
    );
    const found = categories.find((cat) => cat.slug === slug);
    setCategory(found || null);
    setLoading(false);
  }, [slug]);

  if (loading) {
    return null; // or a spinner if you prefer
  }

  if (!category) {
    return <Navigate to="/not-found" replace />;
  }

  // Helper to ensure Cloudinary URLs
  const getCloudinaryUrl = (url) =>
    url.startsWith('http')
      ? url
      : `https://res.cloudinary.com/dwoubdppz/image/upload${url}`;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{category.metaTitle}</title>
        <meta name="description" content={category.metaDescription} />
      </Helmet>
      {/* Hero Section */}
      <div className="relative h-[45vh] w-full flex items-center justify-center">
        <img
          src={getCloudinaryUrl(category.featuredImage)}
          alt={category.name}
          className="object-cover absolute inset-0 z-0 w-full h-full"
        />
        <div className="absolute inset-0 z-10 bg-black/50" />
        <div className="relative z-20 px-4 mx-auto max-w-3xl text-center text-white">
          <h1 className="mb-3 text-4xl font-bold tracking-wide uppercase drop-shadow-lg md:text-6xl">
            {category.name}
          </h1>
          <p className="mb-4 text-lg font-medium drop-shadow">
            {category.shortDescription}
          </p>
          <Link
            to={category.ctaLink}
            className="inline-block px-8 py-3 mt-2 font-semibold text-white bg-blue-700 rounded-lg shadow-lg transition-all duration-200 hover:bg-blue-800">
            {category.ctaText}
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-12 mx-auto max-w-4xl">
        <section className="mb-10">
          <h2 className="mb-4 text-2xl font-bold text-gray-900">
            About {category.name}
          </h2>
          <p className="mb-6 text-lg text-gray-700">
            {category.longDescription}
          </p>
          <ul className="pl-6 mb-6 list-disc text-gray-700">
            {category.whyChooseUs.map((reason, i) => (
              <li key={i}>{reason}</li>
            ))}
          </ul>
        </section>

        <section className="mb-10">
          <h3 className="mb-2 text-xl font-semibold text-gray-900">
            Services Offered
          </h3>
          <ul className="pl-6 list-disc text-gray-700">
            {category.servicesOffered.map((service, i) => (
              <li key={i}>{service}</li>
            ))}
          </ul>
        </section>

        <section className="mb-10">
          <h3 className="mb-2 text-xl font-semibold text-gray-900">
            Our Process
          </h3>
          <ol className="pl-6 list-decimal text-gray-700">
            {category.processOverview.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </section>

        <section className="mb-10">
          <h3 className="mb-2 text-xl font-semibold text-gray-900">
            Frequently Asked Questions
          </h3>
          <ul className="divide-y divide-gray-200">
            {category.faqs.map((faq, i) => (
              <li key={i} className="py-3">
                <strong className="block text-gray-800">
                  Q: {faq.question}
                </strong>
                <span className="block mt-1 text-gray-600">
                  A: {faq.answer}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-10">
          <h3 className="mb-2 text-xl font-semibold text-gray-900">
            Testimonials
          </h3>
          <ul className="divide-y divide-gray-200">
            {category.testimonials.map((t, i) => (
              <li key={i} className="py-3">
                <span className="block text-gray-800">"{t.text}"</span>
                <span className="block mt-1 text-gray-600">
                  - {t.author}
                  {t.location ? `, ${t.location}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-10">
          <h3 className="mb-2 text-xl font-semibold text-gray-900">Gallery</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {category.gallery.map((img, i) => (
              <img
                key={i}
                src={getCloudinaryUrl(img)}
                alt={`${category.name} example ${i + 1}`}
                className="object-cover w-full h-56 rounded shadow"
                loading="lazy"
              />
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h3 className="mb-2 text-xl font-semibold text-gray-900">
            Explore More
          </h3>
          <ul className="flex flex-wrap gap-3">
            {category.internalLinks.map((link, i) => (
              <li key={i}>
                <Link
                  to={link.url}
                  className="inline-block px-4 py-2 text-blue-700 bg-blue-50 rounded transition hover:bg-blue-100">
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
